import { type ReactNode, useEffect, useState } from "react";

interface BottomModalProps {
	show: boolean;
	onClose: () => void;
	children: ReactNode;
	maxWidth?: string;
	position?: "fixed" | "absolute";
}

/**
 * Reusable bottom modal component with slide-up animation
 */
export const BottomModal = ({
	show,
	onClose,
	children,
	maxWidth = "100%",
	position = "fixed",
}: BottomModalProps) => {
	const [isRendered, setIsRendered] = useState(false);
	const [isAnimating, setIsAnimating] = useState(false);

	useEffect(() => {
		if (show) {
			setIsRendered(true);
			// Small delay to allow DOM to render before animation starts
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					setIsAnimating(true);
				});
			});
		} else {
			setIsAnimating(false);
			// Wait for animation to finish before unmounting
			const timer = setTimeout(() => setIsRendered(false), 300);
			return () => clearTimeout(timer);
		}
	}, [show]);

	if (!isRendered) return null;

	return (
		<>
			{/* Backdrop */}
			<div
				className={`${position} inset-0 w-full h-full bg-[rgba(45,52,63,0.3)] transition-opacity duration-300 ease-out z-50 ${
					isAnimating ? "opacity-100" : "opacity-0"
				}`}
				onClick={onClose}
			/>

			{/* Modal */}
			<div
				className={`${position} bottom-0 left-1/2 -translate-x-1/2 transition-transform duration-300 ease-out z-50 ${
					isAnimating ? "translate-y-0" : "translate-y-full"
				}`}
				style={{ maxWidth: maxWidth, width: "100%" }}
			>
				<div className="flex flex-col w-full rounded-tl-[24px] rounded-tr-[24px] bg-color-White shadow-[0px_1px_12px_0px_rgba(0,0,0,0.04)]">
					{/* Home indicator */}
					<div className="w-[40px] h-[5px] rounded-[5px] bg-color-Grey900 mx-auto mt-2 mb-0" />

					{/* Content */}
					{children}
				</div>
			</div>
		</>
	);
};
